import { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FileText, History, Download, Eye, Rocket, RefreshCw, CheckCheck, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** "1m 23s" / "42s" / "2m" / "—" — readable formatting for the read-duration column. */
function formatReadDuration(ms) {
  if (ms == null || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

/**
 * Super-Admin tab to edit and publish new versions of the Service Agreement,
 * plus inspect/export the per-version acceptance audit log.
 */
export default function ServiceAgreementAdminTab({ headers, language }) {
  const tt = (en, es) => (language === 'es' ? es : en);

  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [current, setCurrent] = useState(null);

  // Editor state
  const [version, setVersion] = useState('');
  const [summary, setSummary] = useState('');
  const [contentEn, setContentEn] = useState('');
  const [contentEs, setContentEs] = useState('');
  const [previewLang, setPreviewLang] = useState('en');
  const [publishing, setPublishing] = useState(false);

  // Audit log dialog
  const [logVersion, setLogVersion] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logLoading, setLogLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/service-agreement/list`, { headers });
      const list = r.data?.agreements || [];
      setVersions(list);
      const curr = list.find(v => v.is_current);
      setCurrent(curr || null);
      if (curr && !contentEn && !contentEs) {
        // Pre-fill editor with current content as a starting point
        setContentEn(curr.content_en || '');
        setContentEs(curr.content_es || '');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const suggestNextVersion = (currentVer) => {
    if (!currentVer) return '1.0';
    const parts = String(currentVer).split('.');
    if (parts.length >= 2) {
      const minor = parseInt(parts[1], 10);
      if (!Number.isNaN(minor)) return `${parts[0]}.${minor + 1}`;
    }
    return `${currentVer}.1`;
  };

  const publish = async () => {
    if (!version.trim()) { toast.error(tt('Version is required', 'La versión es obligatoria')); return; }
    if (contentEn.trim().length < 10 || contentEs.trim().length < 10) {
      toast.error(tt('Both EN and ES content are required', 'Se requiere contenido en EN y ES'));
      return;
    }
    if (!window.confirm(tt(
      `Publish version ${version}? This will become the active agreement and all users will be asked to accept it on their next visit.`,
      `¿Publicar versión ${version}? Pasará a ser el acuerdo activo y se pedirá aceptación a todos los usuarios en su próxima visita.`,
    ))) return;

    setPublishing(true);
    try {
      await axios.post(
        `${API}/admin/service-agreement/publish`,
        { version: version.trim(), content_en: contentEn, content_es: contentEs, summary },
        { headers },
      );
      toast.success(tt(`Published v${version}`, `Publicada v${version}`));
      setVersion('');
      setSummary('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setPublishing(false); }
  };

  const setAsCurrent = async (v) => {
    if (!window.confirm(tt(`Make v${v} the current agreement?`, `¿Establecer v${v} como acuerdo actual?`))) return;
    try {
      await axios.post(`${API}/admin/service-agreement/${v}/set-current`, {}, { headers });
      toast.success(tt('Updated', 'Actualizado'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openLog = async (v) => {
    setLogVersion(v);
    setLogLoading(true);
    try {
      const r = await axios.get(`${API}/admin/service-agreement/${v}/acceptances?limit=200`, { headers });
      setLogRows(r.data?.acceptances || []);
      setLogTotal(r.data?.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLogLoading(false); }
  };

  const downloadCsv = async (v) => {
    const url = `${API}/admin/service-agreement/${v}/acceptances/csv`;
    try {
      const blob = await axios.get(url, { headers, responseType: 'blob' });
      const obj = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = obj;
      a.download = `wakacasa-agreement-acceptances-v${v}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(obj);
    } catch { toast.error('Failed'); }
  };

  const loadCurrentIntoEditor = () => {
    if (!current) return;
    setContentEn(current.content_en || '');
    setContentEs(current.content_es || '');
    setVersion(suggestNextVersion(current.version));
    toast.success(tt('Loaded current into editor', 'Cargado en el editor'));
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6" data-testid="service-agreement-admin-tab">
      {/* Header card */}
      <Card className="border-2 border-[#D4AF37]/30 bg-gradient-to-br from-amber-50/30 to-white">
        <CardContent className="p-5 flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-[#0B192C]" />
          </div>
          <div className="flex-1">
            <p className="font-heading text-lg font-semibold text-[#0B192C]">
              {tt('Service Agreement', 'Acuerdo de Servicio')}
              {current && <Badge className="ml-2 bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]">v{current.version} · {tt('Active', 'Activo')}</Badge>}
            </p>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {tt(
                'Edit the bilingual terms below and publish a new version. All users will be force-asked to accept the new version on their next session.',
                'Edita los términos bilingües y publica una nueva versión. A todos los usuarios se les pedirá aceptarla en su próxima sesión.',
              )}
            </p>
            {current && (
              <p className="text-[11px] text-slate-400 mt-2">
                {tt('Published', 'Publicada')} {new Date(current.published_at).toLocaleString()} · {tt('by', 'por')} {current.published_by}
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={loadCurrentIntoEditor} disabled={!current} data-testid="sa-load-current-btn">
            <ChevronRight className="w-3.5 h-3.5 mr-1" />{tt('Load current into editor', 'Cargar en editor')}
          </Button>
        </CardContent>
      </Card>

      {/* Editor */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="font-semibold text-sm text-[#0B192C] flex items-center gap-2"><Rocket className="w-4 h-4" />{tt('Publish New Version', 'Publicar Nueva Versión')}</p>
          </div>

          <div className="grid sm:grid-cols-[160px_1fr] gap-3">
            <div>
              <Label className="text-xs">{tt('Version', 'Versión')} *</Label>
              <Input value={version} onChange={e => setVersion(e.target.value)} placeholder="e.g. 1.1" className="mt-1" data-testid="sa-version-input" />
            </div>
            <div>
              <Label className="text-xs">{tt('Summary of changes (internal)', 'Resumen de cambios (interno)')}</Label>
              <Input value={summary} onChange={e => setSummary(e.target.value)} placeholder={tt('e.g. Added lawyer retainer clause', 'ej. Cláusula de contrato con abogado')} className="mt-1" data-testid="sa-summary-input" />
            </div>
          </div>

          <Tabs defaultValue="en">
            <TabsList className="bg-slate-100 p-1 rounded-lg">
              <TabsTrigger value="en" className="rounded-md" data-testid="sa-editor-tab-en">English</TabsTrigger>
              <TabsTrigger value="es" className="rounded-md" data-testid="sa-editor-tab-es">Español</TabsTrigger>
            </TabsList>
            <TabsContent value="en">
              <EditorPane content={contentEn} onChange={setContentEn} lang="en" />
            </TabsContent>
            <TabsContent value="es">
              <EditorPane content={contentEs} onChange={setContentEs} lang="es" />
            </TabsContent>
          </Tabs>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <p className="text-[11px] text-slate-400">{tt('Markdown supported (headings, lists, links, bold).', 'Soporta Markdown (títulos, listas, enlaces, negritas).')}</p>
            <Button
              onClick={publish}
              disabled={publishing || !version.trim()}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold"
              data-testid="sa-publish-btn"
            >
              <Rocket className="w-4 h-4 mr-1" />{publishing ? tt('Publishing…', 'Publicando…') : tt('Publish new version', 'Publicar nueva versión')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Version history */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 pb-2">
            <p className="font-semibold text-sm flex items-center gap-2"><History className="w-4 h-4" />{tt('Version History', 'Historial de Versiones')} ({versions.length})</p>
            <Button size="sm" variant="ghost" onClick={load} className="h-8" data-testid="sa-history-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tt('Version', 'Versión')}</TableHead>
                  <TableHead>{tt('Status', 'Estado')}</TableHead>
                  <TableHead>{tt('Summary', 'Resumen')}</TableHead>
                  <TableHead>{tt('Published', 'Publicada')}</TableHead>
                  <TableHead className="text-right">{tt('Actions', 'Acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map(v => (
                  <TableRow key={v.version} data-testid={`sa-version-row-${v.version}`}>
                    <TableCell className="font-mono font-bold">v{v.version}</TableCell>
                    <TableCell>
                      {v.is_current
                        ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{tt('Active', 'Activo')}</Badge>
                        : <Badge className="bg-slate-200 text-slate-600 text-[10px]">{tt('Archived', 'Archivado')}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[260px] truncate">{v.summary || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-400">
                      {v.published_at ? new Date(v.published_at).toLocaleDateString() : ''}
                      <div className="text-[10px] text-slate-400 truncate">{v.published_by}</div>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openLog(v.version)} className="h-7 px-2" data-testid={`sa-log-btn-${v.version}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" />{tt('Log', 'Registro')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadCsv(v.version)} className="h-7 px-2" data-testid={`sa-csv-btn-${v.version}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {!v.is_current && (
                        <Button size="sm" variant="ghost" onClick={() => setAsCurrent(v.version)} className="h-7 px-2 text-emerald-700 hover:bg-emerald-50" data-testid={`sa-setcurrent-btn-${v.version}`}>
                          <CheckCheck className="w-3.5 h-3.5 mr-1" />{tt('Set current', 'Activar')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {versions.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">{tt('No versions yet', 'Sin versiones')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Acceptance log dialog */}
      <Dialog open={!!logVersion} onOpenChange={(v) => { if (!v) { setLogVersion(null); setLogRows([]); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="sa-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {tt(`Acceptances for v${logVersion}`, `Aceptaciones de v${logVersion}`)} ({logTotal})
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 border border-slate-200 rounded-lg bg-slate-50">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead>{tt('Accepted at', 'Aceptado el')}</TableHead>
                  <TableHead>{tt('User', 'Usuario')}</TableHead>
                  <TableHead>{tt('Role', 'Rol')}</TableHead>
                  <TableHead>{tt('Source', 'Origen')}</TableHead>
                  <TableHead className="text-right">{tt('Read time', 'Tiempo de lectura')}</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>User Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">{tt('Loading…', 'Cargando…')}</TableCell></TableRow>
                )}
                {!logLoading && logRows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">{tt('No acceptances yet', 'Sin aceptaciones')}</TableCell></TableRow>
                )}
                {logRows.map((r, i) => (
                  <TableRow key={r.id || i} data-testid={`sa-log-row-${i}`}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(r.accepted_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{r.user_email}</TableCell>
                    <TableCell className="text-xs"><Badge className="bg-slate-200 text-slate-700 text-[10px] capitalize">{r.user_role}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{r.source}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-slate-600" data-testid={`sa-log-readtime-${i}`}>
                      {formatReadDuration(r.read_duration_ms)}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{r.ip || '—'}</TableCell>
                    <TableCell className="text-[10px] text-slate-400 max-w-[260px] truncate" title={r.user_agent}>{r.user_agent || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadCsv(logVersion)} data-testid="sa-log-csv-btn">
              <Download className="w-4 h-4 mr-1" />{tt('Export CSV', 'Exportar CSV')}
            </Button>
            <Button onClick={() => setLogVersion(null)} className="bg-[#0B192C] text-[#D4AF37]">
              {tt('Close', 'Cerrar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview switcher hint for editor */}
      <div className="hidden">{previewLang}{setPreviewLang}</div>
    </div>
  );
}

function EditorPane({ content, onChange, lang }) {
  return (
    <div className="grid lg:grid-cols-2 gap-3 mt-3">
      <div>
        <Label className="text-[11px] text-slate-500 uppercase tracking-wider">Markdown ({lang.toUpperCase()})</Label>
        <Textarea
          value={content}
          onChange={(e) => onChange(e.target.value)}
          rows={20}
          className="mt-1 font-mono text-xs leading-relaxed"
          data-testid={`sa-md-textarea-${lang}`}
        />
      </div>
      <div>
        <Label className="text-[11px] text-slate-500 uppercase tracking-wider">{lang === 'es' ? 'Vista previa' : 'Preview'}</Label>
        <div className="mt-1 border border-slate-200 bg-white rounded-md p-3 h-[460px] overflow-auto" data-testid={`sa-preview-${lang}`}>
          <article className="prose prose-sm prose-slate max-w-none prose-headings:font-heading prose-headings:text-[#0B192C] prose-p:text-slate-600">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
          </article>
        </div>
      </div>
    </div>
  );
}
